import { Schema, string } from 'yup';

/**
 * Lightweight clone durante `Schema.prototype.resolve()`.
 *
 * Il problema: `resolve()` (riga 609 di yup) chiama `schema.clone()` per
 * ogni schema con condizioni. `clone()` usa `nanoclone` (deep clone ricorsivo)
 * per copiare `this.spec`, creando una `new Map()` per il tracking circolare
 * e ricorrendo su ogni proprietà. Questo è il costo principale del clone.
 *
 * Questa patch sostituisce `nanoclone(spec)` con shallow copy durante resolve:
 * - `{ ...this.spec, ...spec }` + `{ ...spec.meta }` al posto di `nanoclone`
 * - Tutto il resto (whitelist, blacklist, deps, tests, transforms, ecc.)
 *   viene copiato normalmente come nel clone originale
 *
 * Perché lo shallow copy di spec è safe:
 * - Tutti i valori di `spec` sono primitivi (strip, strict, nullable,
 *   optional, coerce, label, abortEarly, recursive) tranne `meta`
 * - `meta` contiene oggetti (parentDeps, customErrorDeps, enabled, ecc.)
 *   ma i metodi che lo modificano (`schema.meta({...})`) usano
 *   `Object.assign(next.spec.meta, args)` che scrive proprietà al primo
 *   livello del meta clonato, senza mutare i valori nested condivisi
 * - `schema.meta()` dentro `withMutation` non chiama `clone()` (ritorna `this`),
 *   quindi muta `spec.meta` in-place — ma in quel caso `spec` è già la copia
 *   del clone iniziale di `resolve()`, non l'originale
 *
 * NOTA: questa patch dipende dalla struttura interna di Yup (@favoloso/yup 1.4.0-fv.4).
 * Se Yup viene aggiornato, verificare che la struttura interna sia compatibile.
 * Il sanity check all'avvio protegge da rotture silenziose.
 */

/**
 * Verifica che la struttura interna di Schema sia compatibile con la patch.
 * Usa un'istanza concreta (StringSchema) perché Schema è astratto.
 */
function isSchemaStructureCompatible(): boolean {
  const proto = Schema.prototype as any;
  if (typeof proto.clone !== 'function') return false;
  if (typeof proto.resolve !== 'function') return false;

  // Verifichiamo su un'istanza concreta (Schema è astratto, non istanziabile)
  const testSchema = string() as any;
  const required = [
    '_whitelist',
    '_blacklist',
    'deps',
    'tests',
    'transforms',
    'conditions',
    'spec',
  ];
  for (const prop of required) {
    if (!(prop in testSchema)) return false;
  }

  return true;
}

if (isSchemaStructureCompatible()) {
  applyLightweightResolvePatch();
} else {
  console.warn(
    '[lightweightResolve] Struttura interna di Yup non compatibile. ' +
      'La patch non è stata applicata. Verificare la versione di Yup.',
  );
}

function applyLightweightResolvePatch() {
  let _resolveDepth = 0;
  const schemasCloningForResolve = new WeakSet<object>();

  const originalClone = Schema.prototype.clone;
  const originalResolve = Schema.prototype.resolve;

  function cloneConditionsForResolve(schema: any) {
    if (schemasCloningForResolve.delete(schema)) {
      return [];
    }

    const conditions = schema.conditions;
    return conditions.length > 0 ? [...conditions] : [];
  }

  Schema.prototype.clone = function (this: Schema, spec?: any) {
    // Fast path originale per withMutation
    if ((this as any)._mutate) {
      if (spec) Object.assign(this.spec, spec);
      return this;
    }

    // Clone normale se non siamo dentro resolve()
    if (_resolveDepth === 0) return originalClone.call(this, spec);

    // --- Lightweight clone per resolve() ---
    // Identico al clone originale TRANNE per spec:
    // nanoclone(spec) → shallow copy + shallow meta.
    const next = Object.create(Object.getPrototypeOf(this));

    next.type = this.type;
    next._typeCheck = (this as any)._typeCheck;

    // Copiati come l'originale
    next._whitelist = (this as any)._whitelist.clone();
    next._blacklist = (this as any)._blacklist.clone();
    next.internalTests = { ...(this as any).internalTests };
    next.exclusiveTests = { ...(this as any).exclusiveTests };
    next.deps = [...(this as any).deps];
    next.tests = [...(this as any).tests];
    next.transforms = [...(this as any).transforms];

    // Nel clone iniziale di resolve() Yup svuota subito `conditions`, quindi
    // manteniamo il fast path a [] senza copiare l'array. Nei cloni successivi
    // invece bisogna preservare eventuali condition pushate durante
    // withMutation (es. parentWhen da CustomWarnings), altrimenti una condition
    // successiva che crea un nuovo schema le perderebbe.
    next.conditions = cloneConditionsForResolve(this);

    // L'unico cambiamento: shallow copy di spec invece di nanoclone.
    // nanoclone crea `new Map()` + ricorre su ogni proprietà + isSchema()
    // check per ogni valore. Lo shallow è sufficiente perché tutti i valori
    // di spec sono primitivi tranne meta, e meta viene modificato solo
    // via Object.assign al primo livello.
    next.spec = { ...this.spec, ...spec };
    if (next.spec.meta) next.spec.meta = { ...next.spec.meta };

    return next;
  };

  Schema.prototype.resolve = function (this: Schema, options: any) {
    if (!(this as any).conditions.length) return this;

    schemasCloningForResolve.add(this);

    _resolveDepth++;
    try {
      return originalResolve.call(this, options);
    } finally {
      _resolveDepth--;
      schemasCloningForResolve.delete(this);
    }
  };
}
